import type { CompanyDetail, StaffRole } from '@planuze/admin-contracts';
import { Button, StatusBadge, toast } from '@planuze/ui';
import { Copy, Lock, Unlock } from 'lucide-react';
import { useState } from 'react';
import { type CompaniesContent, useCompaniesContent } from '../../content/sections/companies.js';
import { fmtDate, fmtNumber } from '../../lib/format.js';
import { type SummaryMetric, SummaryMetrics } from '../detail-summary.js';
import { BlockCompanyModal } from './block-company-modal.js';
import { statusLabel, statusTone } from './company-status.js';
import { UnblockCompanyModal } from './unblock-company-modal.js';

const WRITE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

interface Props {
  readonly company: CompanyDetail;
  readonly staffRole: StaffRole;
}

/* Faixa-resumo do detalhe (ADR-0707): condensa contagens/estado derivados do DTO já
   carregado (sem chamada extra). Assentos e status da assinatura só aparecem quando há
   subscription — nunca inventamos valor ausente. */
function summaryItems(company: CompanyDetail, copy: CompaniesContent): SummaryMetric[] {
  const { subscription } = company;
  const items: SummaryMetric[] = [
    { label: copy.common.members, value: fmtNumber(company.memberCount) },
  ];
  if (subscription !== null) {
    items.push({ label: copy.detailHeader.summarySeats, value: fmtNumber(subscription.quantity) });
  }
  items.push({
    label: copy.detailHeader.summarySubscription,
    value: subscription?.status ?? copy.detailHeader.summaryNoSubscription,
  });
  items.push({ label: copy.common.createdAt, value: fmtDate(company.createdAt) });
  return items;
}

/* ID da empresa copiável: identificador técnico útil para suporte/cross-lookup. O toast
   é o feedback de cópia (sem estado transitório). */
function CopyableId({ id, copy }: { readonly id: string; readonly copy: CompaniesContent }) {
  const doCopy = (): void => {
    navigator.clipboard
      .writeText(id)
      .then(() => toast.success(copy.detailHeader.copySuccess))
      .catch(() => toast.error(copy.detailHeader.copyError));
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      title={copy.detailHeader.copyIdTitle}
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 font-mono text-muted-foreground text-xs transition-colors hover:bg-accent/15 hover:text-foreground"
    >
      <Copy className="size-3.5" aria-hidden="true" />
      <span>{id}</span>
    </button>
  );
}

/* Header do detalhe: nome + status + ID copiável + faixa-resumo + ação block/unblock
   (apenas super_admin/support, §4.18 RBAC declarativo). Prop `staffRole` (não `role`)
   evita colisão com o atributo ARIA homônimo no linter. */
export function CompanyDetailHeader({ company, staffRole }: Props) {
  const copy = useCompaniesContent();
  const canWrite = WRITE_ROLES.includes(staffRole);
  const [blockOpen, setBlockOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);
  const isBlocked = company.status === 'blocked';
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">{company.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge variant={statusTone(company.status)}>
              {statusLabel(company.status, copy.status)}
            </StatusBadge>
            <span
              className="font-mono text-muted-foreground text-xs"
              title={copy.detailHeader.slugTitle}
            >
              {company.slug}
            </span>
            <CopyableId id={company.id} copy={copy} />
          </div>
        </div>
        {canWrite &&
          (isBlocked ? (
            <Button onClick={() => setUnblockOpen(true)}>
              <Unlock className="mr-2 size-4" aria-hidden="true" />
              {copy.detailHeader.unblock}
            </Button>
          ) : (
            <Button variant="destructive" onClick={() => setBlockOpen(true)}>
              <Lock className="mr-2 size-4" aria-hidden="true" />
              {copy.detailHeader.block}
            </Button>
          ))}
      </div>
      <SummaryMetrics items={summaryItems(company, copy)} />
      <BlockCompanyModal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        companyId={company.id}
        companyName={company.name}
      />
      <UnblockCompanyModal
        open={unblockOpen}
        onClose={() => setUnblockOpen(false)}
        companyId={company.id}
        companyName={company.name}
      />
    </div>
  );
}
